/**
 * Layout commun aux pages du backoffice DSI/admin.
 *
 * - Garde : ouvert à super_admin et admin uniquement.
 * - Sidebar fixe (desktop ≥ lg) + bottom-nav fixed mobile (< lg).
 * - Topbar avec nom du tenant + ThemeToggle + bouton retour dashboard.
 *
 * Le contenu enfant rend la page courante (Outlet de react-router).
 *
 * Refonte R3 (mai 2026) :
 *   - Topbar en glass avec halo radial tech localisé
 *   - LogoMark composant R2 (Landmark + gradient tech)
 *   - NavLink en pill glass active (accent tech)
 *   - Background page géré par R1 (halos radiaux automatiques sur body)
 *   - Loading state moderne en Card glass + spinner glow
 *
 * Refonte L13.5 (21 mai 2026) — mobile-first :
 *   - Sortie du pattern nav horizontale scrollable mobile (problème : zone
 *     contenu noir/vide constatée sur iPhone XR malgré le hotfix R13.8).
 *   - Remplacé par bottom-nav fixed bottom-0, 4 items en grille égale,
 *     avec safe-area-inset-bottom pour iPhone notch et indicateur barre
 *     tech au-dessus de l'item actif (pattern iOS/Android natif).
 *   - Padding-bottom 24 sur le wrapper main pour réserver l'espace nav.
 *   - Topbar compactée mobile (label « Tableau de bord » masqué < sm).
 *   - Sidebar desktop (lg+) inchangée.
 */

import { useState } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AppHeader } from '@/components/AppHeader';
import { LogoMark, SectionEyebrow, Button } from '@/components/ui';
import { Archive, ArrowLeft, Bell, BookUser, Building2, CalendarClock, Clock, Database, FileText, FolderKanban, GitBranch, Landmark, LayoutGrid, Mail, MapPin, MoreHorizontal, Plug, Settings2, ShieldAlert, ShieldCheck, Users, X, Upload } from 'lucide-react';
import type { UserRole } from '@roronoa-collectivites/shared-types';

const ADMIN_ALLOWED_ROLES: UserRole[] = ['super_admin', 'admin'];

/**
 * Familles de la navigation admin.
 *
 * L'admin comptait 18 entrées À PLAT : les listes métier (Lieux, Prestataires,
 * Annuaire…) se retrouvaient coincées entre « Export données » et
 * « Sauvegardes », sans qu'on voie qu'elles forment une famille. On regroupe en
 * trois blocs lisibles — aucune route ne change, c'est de la navigation.
 *
 * Le libellé « Listes & annuaires » a été préféré à « Référentiels » (jargon
 * secteur public) et « Bibliothèques » (jargon informatique) : un agent qui
 * cherche où ajouter un fournisseur doit cliquer du premier coup.
 */
const FAMILLES = ['listes', 'acces', 'configuration'] as const;
type Famille = (typeof FAMILLES)[number];

const FAMILLE_LABEL: Record<Famille, string> = {
  listes: 'Listes & annuaires',
  acces: 'Accès & sécurité',
  configuration: 'Configuration',
};

interface NavItem {
  label: string;
  to: string;
  icon: typeof Users;
  /** Rôles qui voient cette entrée dans la sidebar. */
  roles: UserRole[];
  /** Si true, l'entrée n'apparaît PAS dans la bottom-nav mobile (sidebar desktop only). */
  desktopOnly?: boolean;
  /** Regroupement visuel dans la sidebar. */
  famille: Famille;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Utilisateurs',
    to: '/admin/utilisateurs',
    famille: 'acces',
    icon: Users,
    roles: ['super_admin', 'admin'],
  },
  {
    label: 'Invitations',
    to: '/admin/invitations',
    famille: 'acces',
    icon: Mail,
    roles: ['super_admin', 'admin'],
  },
  {
    label: 'Permissions',
    to: '/admin/permissions',
    famille: 'acces',
    icon: ShieldCheck,
    roles: ['super_admin', 'admin'], // visible aux admins (lecture), édition réservée super_admin (gardée API-side)
  },
  {
    // Bascule DGS — sa place est à côté de Permissions : c'est la même nature
    // (qui a le droit de quoi). desktopOnly : réglage posé, pas mobile.
    // Refonte UX, chantier 4 : « Profils et droits » remplace « Périmètres »
    // (modèles Maire / Adjoint délégué / Administrateur, Aucun-Lecture-Gestion).
    // L'adresse /admin/perimetres reste valide.
    label: 'Profils et droits',
    to: '/admin/profils',
    famille: 'acces',
    icon: ShieldCheck,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Déports (conflits d'intérêts) : décret 2014-90 ; urbanisme C. urb. L422-7.
    label: 'Déports',
    to: '/admin/deports',
    famille: 'acces',
    icon: ShieldAlert,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    label: 'Audit log',
    to: '/admin/audit-log',
    famille: 'acces',
    icon: FileText,
    roles: ['super_admin', 'admin'],
  },
  {
    // ── UNE seule entrée (30/07) ──────────────────────────────────────────
    //
    // Il y en avait DEUX — « Export données » et « Import données » — avec la
    // MÊME liste de référentiels et des boutons différents. Henrico : « une
    // seule page, import export et modèle, ça évite d'avoir quarante menus ».
    //
    // Les deux anciennes URL restent servies et redirigent : un favori de la
    // mairie ne doit pas tomber sur une page blanche.
    label: 'Données',
    to: '/admin/donnees',
    famille: 'configuration',
    icon: Database,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Vague 4a — machine à états (mig 244) : les circuits d'étapes
    // (futur moteur de projet 10.1, cycle devis 3.13).
    label: 'Circuits',
    to: '/admin/workflows',
    famille: 'configuration',
    icon: GitBranch,
    roles: ['super_admin', 'admin'],
  },
  {
    // Refonte UX, chantier 5 — tout ce que VPC fait tout seul, en phrases.
    label: 'Automatismes',
    to: '/admin/automatismes',
    famille: 'configuration',
    icon: Clock,
    roles: ['super_admin', 'admin'],
  },
  // Projets : tuile du lanceur depuis le 24/09 (Q1 tranché) ; adresse /admin/projets conservée.
  {
    // L13.7 — édition profil tenant (nom, SIRET, adresse, coordonnées de contact)
    label: 'Mairie',
    to: '/admin/parametres-mairie',
    famille: 'configuration',
    icon: Settings2,
    roles: ['super_admin', 'admin'],
  },
  {
    // Référentiel de lieux communaux (S4-a, mig 203). Réglage structurant,
    // desktop only pour préserver la bottom-nav mobile.
    label: 'Lieux',
    to: '/admin/lieux',
    famille: 'listes',
    icon: MapPin,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Annuaire des tiers partagé (mig 213) : fournisseurs, clients, organismes…
    // filtrable par casquette, réutilisé par les briques (devis technique, etc.).
    label: 'Annuaire',
    to: '/admin/annuaire',
    famille: 'listes',
    icon: BookUser,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // L13.9 — statut sauvegardes BDD (desktop uniquement pour économiser la
    // bottom-nav mobile à 5 items — consultable via URL directe sur mobile).
    label: 'Sauvegardes',
    to: '/admin/sauvegardes',
    famille: 'configuration',
    icon: Database,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Diagnostic & test du raccordement Chorus Pro. Visible admin + super_admin
    // (donc accessible en mode hotline, où le super_admin agit avec le rôle mairie).
    label: 'Chorus Pro',
    to: '/admin/chorus',
    famille: 'configuration',
    icon: Plug,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Activation des briques (modules) de la commune — pilotage maire + fournisseur.
    label: 'Modules',
    to: '/admin/modules',
    famille: 'configuration',
    icon: LayoutGrid,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Catalogue des notifications — « qui reçoit quoi ». Transverse à toutes les
    // briques, donc ici et pas dans la RH. desktopOnly : la bottom-nav mobile est
    // limitée à 5 items, et ce catalogue est un écran de consultation posée.
    label: 'Notifications',
    to: '/admin/notifications',
    famille: 'configuration',
    icon: Bell,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Échéances & rappels — socle réglable (Lot B, mig 211). Transverse comme
    // les notifications. desktopOnly pour préserver la bottom-nav mobile.
    label: 'Échéances',
    to: '/admin/echeances',
    famille: 'listes',
    icon: CalendarClock,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  {
    // Conservation RGPD — durées de conservation + purge des documents (DPO/maire).
    label: 'Conservation',
    to: '/admin/conservation',
    famille: 'configuration',
    icon: ShieldAlert,
    roles: ['super_admin', 'admin'],
    desktopOnly: true,
  },
  // Refonte UX, chantier 1 (24/09) — retirés de ce menu, adresses CONSERVÉES :
  //   /scolaire/parametres (doublon : Scolaire → Paramètres), /admin/rh-paie (doublon :
  //   brique RH), /admin/relecteurs (doublon : page Arrêtés), /admin/prestataires
  //   (lien depuis Paramètres de la cantine), /admin/deliberations (lien depuis
  //   Délibérations du conseil), /admin/archives (tuile Archives du lanceur).
];

export function AdminLayout() {
  usePageTitle('Administration');
  const { user, tenant, loading } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div
          className="rounded-r-2xl border border-border bg-surface/55 px-6 py-5 backdrop-blur-glass shadow-glass flex items-center gap-3"
          role="status"
          aria-label="Chargement"
        >
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-tech border-t-transparent" aria-hidden="true" />
          <span className="text-sm text-ink-muted">Chargement du backoffice…</span>
        </div>
      </div>
    );
  }
  if (!user || !ADMIN_ALLOWED_ROLES.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const visibleNav = NAV_ITEMS.filter((it) => it.roles.includes(user.role));

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Administration" accent="tech" />

      {/* ===== Layout principal ===== */}
      {/* pb-24 lg:pb-6 → réserve l'espace pour la bottom-nav fixed mobile */}
      <div className="mx-auto flex flex-col lg:flex-row max-w-7xl gap-6 px-4 py-6 pb-24 sm:px-6 lg:pb-6">
        {/* ===== Sidebar desktop (lg+) : NavLinks en pills glass ===== */}
        <aside className="hidden w-56 shrink-0 lg:block" aria-label="Navigation backoffice">
          <nav className="sticky top-24 space-y-4">
            {FAMILLES.map((fam) => {
              const items = visibleNav.filter((it) => it.famille === fam);
              if (items.length === 0) return null;
              return (
                <div key={fam} className="space-y-1.5">
                  <p className="px-3.5 text-2xs font-semibold uppercase tracking-r-eyebrow text-ink-dim">
                    {FAMILLE_LABEL[fam]}
                  </p>
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        [
                          'flex h-11 items-center gap-3 rounded-r-lg px-3.5 text-sm font-medium transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                          isActive
                            ? 'border border-tech/30 bg-tech-tint/80 text-tech-fg shadow-[0_0_0_4px_oklch(var(--c-tech)/0.06)]'
                            : 'border border-transparent text-ink-muted hover:bg-surface/60 hover:text-ink hover:border-border',
                        ].join(' ')
                      }
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* ===== Contenu principal ===== */}
        <main id="main-content" className="min-w-0 flex-1 animate-fade-in">
          <Outlet />
        </main>
      </div>

      {/* ===== Bottom-nav mobile fixed (< lg) — pattern iOS/Android natif ===== */}
      {/* 4 entrées principales + un bouton « Plus » qui ouvre TOUTES les autres
          sections (Sauvegardes, Cantine, Modules, RH/Paie…) : AUCUNE entrée
          n'est inaccessible sur mobile. */}
      {(() => {
        const primaires = visibleNav.filter((it) => !it.desktopOnly).slice(0, 4);
        const primairesTo = new Set(primaires.map((it) => it.to));
        const reste = visibleNav.filter((it) => !primairesTo.has(it.to));
        return (
          <>
            {/* Feuille « Plus » (overflow) */}
            {moreOpen && (
              <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-label="Plus d'options">
                <button
                  type="button"
                  className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                  aria-label="Fermer"
                  onClick={() => setMoreOpen(false)}
                />
                <div className="absolute bottom-0 left-0 right-0 rounded-t-2xl border-t border-border bg-surface/95 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] backdrop-blur-glass shadow-glass animate-fade-in">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-ink">Toutes les sections</span>
                    <button type="button" onClick={() => setMoreOpen(false)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-r-lg text-ink-muted hover:bg-surface/60 hover:text-ink" aria-label="Fermer">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    {FAMILLES.map((fam) => {
                      const items = reste.filter((it) => it.famille === fam);
                      if (items.length === 0) return null;
                      return (
                        <div key={fam}>
                          <p className="mb-2 text-2xs font-semibold uppercase tracking-r-eyebrow text-ink-dim">
                            {FAMILLE_LABEL[fam]}
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {items.map((item) => (
                              <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setMoreOpen(false)}
                                className={({ isActive }) =>
                                  [
                                    'flex flex-col items-center justify-center gap-1.5 rounded-r-lg border px-2 py-3 text-2xs font-medium transition-all',
                                    isActive
                                      ? 'border-tech/30 bg-tech-tint/80 text-tech-fg'
                                      : 'border-border bg-surface/40 text-ink-muted hover:bg-surface/60 hover:text-ink',
                                  ].join(' ')
                                }
                              >
                                <item.icon className="h-5 w-5" aria-hidden="true" />
                                <span className="text-center leading-tight">{item.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <nav
              className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-5 border-t border-border bg-surface/85 backdrop-blur-glass lg:hidden"
              style={{
                paddingBottom: 'env(safe-area-inset-bottom)',
                backgroundImage:
                  'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.55), oklch(var(--c-bg) / 0.78))',
              }}
              aria-label="Navigation backoffice (mobile)"
            >
              {primaires.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="group relative flex flex-col items-center justify-center gap-0.5 py-2 text-2xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tech"
                  aria-label={item.label}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-10 rounded-full bg-tech"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={[
                          'inline-flex h-7 w-7 items-center justify-center rounded-r-lg transition-all',
                          isActive
                            ? 'bg-tech-tint text-tech-fg'
                            : 'text-ink-muted group-hover:bg-surface/60 group-hover:text-ink',
                        ].join(' ')}
                        aria-hidden="true"
                      >
                        <item.icon className="h-4 w-4" />
                      </span>
                      <span
                        className={[
                          'leading-tight',
                          isActive ? 'text-tech-fg' : 'text-ink-muted group-hover:text-ink',
                        ].join(' ')}
                      >
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              ))}

              {/* 5e case : bouton « Plus » (ouvre la feuille de toutes les sections) */}
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                className="group relative flex flex-col items-center justify-center gap-0.5 py-2 text-2xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tech"
                aria-label="Plus de sections"
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-r-lg text-ink-muted transition-all group-hover:bg-surface/60 group-hover:text-ink" aria-hidden="true">
                  <MoreHorizontal className="h-4 w-4" />
                </span>
                <span className="leading-tight text-ink-muted group-hover:text-ink">Plus</span>
              </button>
            </nav>
          </>
        );
      })()}
    </div>
  );
}
